import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FunctionsHttpError } from '@supabase/supabase-js';

export interface PotentialMatch {
  pet_id: string;
  pet_name: string;
  photo_url: string | null;
  owner_id: string;
  owner_name: string;
  owner_avatar: string | null;
  location: string;
  species: string;
  breed_name: string | null;
  breed_id: number | null;
  personality_traits: string[] | null;
  age_years: number;
  compatibility_score: number;
  match_reasons: string[];
  distance_estimate: string;
  distance_km?: number;
}

export interface MatchRequest {
  id: string;
  requester_user_id: string;
  requester_pet_id: string;
  target_user_id: string;
  target_pet_id: string;
  match_type: 'playdate' | 'breeding' | 'friendship' | 'adoption';
  message: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  created_at: string;
  responded_at: string | null;
  expires_at: string;
  credits_used: number;
  compatibility_score: number | null;
}

interface MatchErrorPayload {
  code?: 'DUPLICATE_REQUEST' | 'ALREADY_MATCHED' | 'ALREADY_ACCEPTED' | string;
  message?: string;
  error?: string;
}

export const useMatching = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState({
    pet_id: '',
    match_type: 'playdate' as 'playdate' | 'breeding' | 'friendship' | 'adoption',
    max_distance_km: 10,
    limit: 20
  });

  // Buscar matches potenciales
  const findMatches = useMutation({
    mutationFn: async (params: typeof searchParams) => {
      const { data, error } = await supabase.functions.invoke('find-pet-matches', {
        body: params
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      console.log('Matches found:', data.count);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error finding matches',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Crear solicitud de match (¡ESTA ES LA PARTE ARREGLADA!)
  const createMatchRequest = useMutation({
    mutationFn: async (params: {
      requester_pet_id: string;
      target_pet_id: string;
      match_type: string;
      message?: string;
      is_super_match?: boolean;
    }) => {
      const { data, error } = await supabase.functions.invoke('manage-match-request', {
        body: {
          action: 'create',
          ...params
        }
      });

      if (error) {
        if (error instanceof FunctionsHttpError) {
          let errorBody: MatchErrorPayload = {};
          try {
            // Forma recomendada: leer el body del error
            errorBody = await new Response(error.context.body).json();
          } catch (parseErr) {
            try {
              // Fallback: re-read context.body as text
              const bodyText = await new Response(error.context?.body).text();
              errorBody = bodyText ? JSON.parse(bodyText) : {};
            } catch {
              errorBody = { error: error.message };
            }
          }
          throw new Error(JSON.stringify(errorBody));
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: '✅ Match request sent!',
        description: 'Your request has been sent successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['match-requests'] });
      queryClient.invalidateQueries({ queryKey: ['credits-balance'] });
    },
    onError: (error: any) => {
      console.error('Match request error:', error);

      let errorMessage = 'Failed to send match request. Please try again.';
      let title = 'Error';

      try {
        const errorData: MatchErrorPayload = JSON.parse(error.message);

        switch (errorData.code) {
          case 'DUPLICATE_REQUEST':
            title = 'Request Already Sent';
            errorMessage = errorData.message || 'You already have a pending match request with this pet. Please wait for a response or check your sent requests.';
            break;
          case 'ALREADY_MATCHED':
            title = 'Already Matched';
            errorMessage = errorData.message || 'These pets are already matched! Check your Active Matches tab to chat.';
            break;
          case 'ALREADY_ACCEPTED':
            title = 'Already Accepted';
            errorMessage = errorData.message || 'A match request between these pets was already accepted. Check your Active Matches tab.';
            break;
          default:
            if (errorData.message?.includes('Insufficient credits')) {
              errorMessage = "You don't have enough credits to send this request. Please purchase more credits.";
            } else if (errorData.message?.includes('Species mismatch')) {
              errorMessage = 'Cannot send match request. Pets must be of the same species.';
            } else {
              errorMessage = errorData.message || errorData.error || errorMessage;
            }
        }
      } catch {
        errorMessage = error?.message || errorMessage;
      }

      toast({
        title,
        description: errorMessage,
        variant: 'destructive'
      });
    }
  });

  // Aceptar solicitud
  const acceptMatchRequest = useMutation({
    mutationFn: async (requestId: string) => {
      console.log('🔵 Frontend: Calling accept for request:', requestId);

      const { data, error } = await supabase.functions.invoke('manage-match-request', {
        body: {
          action: 'accept',
          request_id: requestId
        }
      });

      console.log('🔵 Frontend: Accept response:', { data, error });

      if (error) {
        console.error('🔴 Frontend: Accept error:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      console.log('🟢 Frontend: Accept success:', data);

      if (data?.warning) {
        // Match ya existía
        toast({
          title: '🎉 Match accepted!',
          description: 'These pets were already matched. Redirecting to existing match...'
        });
      } else {
        // Match nuevo creado
        toast({
          title: '🎉 Match accepted!',
          description: 'Start chatting now!'
        });
      }

      queryClient.invalidateQueries({ queryKey: ['match-requests'] });
      queryClient.invalidateQueries({ queryKey: ['pet-matches'] });
    },
    onError: (error: any) => {
      console.error('🔴 Frontend: Accept onError:', error);

      let errorMessage = 'Failed to accept match. Please try again.';

      try {
        if (error?.message) {
          const parsed = JSON.parse(error.message);
          errorMessage = parsed.details || parsed.error || error.message;
        }
      } catch {
        errorMessage = error?.message || errorMessage;
      }

      toast({
        title: 'Error accepting match',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  });

  // Rechazar solicitud
  const rejectMatchRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.functions.invoke('manage-match-request', {
        body: {
          action: 'reject',
          request_id: requestId
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Request rejected',
        description: 'The match request has been declined'
      });
      queryClient.invalidateQueries({ queryKey: ['match-requests'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error rejecting match',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Eliminar solicitud de match
  const deleteMatchRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('match_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Request deleted',
        description: 'The match request has been removed'
      });
      queryClient.invalidateQueries({ queryKey: ['match-requests'] });
      queryClient.refetchQueries({ queryKey: ['match-requests', 'sent'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting request',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Eliminar match/chat
  const deleteMatch = useMutation({
    mutationFn: async (matchId: string) => {
      const { error } = await supabase
        .from('pet_matches')
        .update({ status: 'inactive' })
        .eq('id', matchId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Match removed',
        description: 'The match has been removed from your inbox'
      });
      queryClient.invalidateQueries({ queryKey: ['pet-matches'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error removing match',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Obtener solicitudes recibidas
  const { data: receivedRequests, isLoading: loadingReceived } = useQuery({
    queryKey: ['match-requests', 'received'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('match_requests')
        .select(`
          *,
          requester_pet:pets!match_requests_requester_pet_id_fkey(id, name, photo_url),
          target_pet:pets!match_requests_target_pet_id_fkey(id, name, photo_url)
        `)
        .eq('target_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MatchRequest[];
    }
  });

  // Obtener solicitudes enviadas
  const { data: sentRequests, isLoading: loadingSent } = useQuery({
    queryKey: ['match-requests', 'sent'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('match_requests')
        .select(`
          *,
          requester_pet:pets!match_requests_requester_pet_id_fkey(id, name, photo_url),
          target_pet:pets!match_requests_target_pet_id_fkey(id, name, photo_url)
        `)
        .eq('requester_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MatchRequest[];
    }
  });

  // Obtener matches activos
  const { data: activeMatches, isLoading: loadingMatches } = useQuery({
    queryKey: ['pet-matches', 'active'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('pet_matches')
        .select(`
        *,
        pet_1:pets!pet_matches_pet_1_id_fkey(id, name, photo_url),
        pet_2:pets!pet_matches_pet_2_id_fkey(id, name, photo_url),
        chat_room:match_chat_rooms!fk_chat_room(id, last_message_at)
      `)
        .or(`user_1_id.eq.${user.id},user_2_id.eq.${user.id}`)
        .eq('status', 'active')
        .order('matched_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  return {
    // Search
    searchParams,
    setSearchParams,
    findMatches,

    // Requests
    createMatchRequest,
    acceptMatchRequest,
    rejectMatchRequest,
    deleteMatchRequest,
    deleteMatch,

    // Data
    receivedRequests,
    sentRequests,
    activeMatches,

    // Loading
    loadingReceived,
    loadingSent,
    loadingMatches
  };
};